// import { GameStats } from '../components/GameStats'
import { ShareButton } from '../components/ShareButton'
import { GuessGrid } from '../components/GuessGrid'
import { useEffect } from 'react'
import { GAME_MODES } from '../constants/gameConfig'
import { useNavigate, useLocation } from 'react-router-dom'
import { useGameSettings } from '../contexts/GameSettingsContext'
import { WordLength, Tile } from '../types/game'
import { motion } from 'framer-motion'
import { trackPageView } from '../services/gameAnalytics'
import { trackEvent } from '../services/hotjar'
import { hasModeBeenPlayed } from '../utils/storage'
import { SaveProgressPrompt } from '../components/SaveProgressPrompt'
import { StreakCard } from '../components/StreakCard'
import { useStats } from '../contexts/StatsContext'
import { recordGamePlayed } from '../utils/playerProgression'
import { formatSecondsTaken } from '../utils/timeUtils'

const API_BASE_URL = import.meta.env.VITE_NEW_WORDS_API_URL || 'http://localhost:8080';

export const GameCompletionPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { settings, updateSettings } = useGameSettings()
  // The game that just finished is already recorded on the server; ask again so the streak is current.
  const { refresh } = useStats()
  useEffect(() => {
    void refresh()
  }, [refresh])

  // Extract data from location state, or provide defaults
  const state = location.state || {}
  const {
    won = false,
    secondsTaken = null,
    guesses = [],
    targetWord = '',
    definition = '',
    setterNote = null,
  } = state

  // Current mode from settings
  const currentMode = settings.selectedMode

  // Track page view with game result information and progression
  useEffect(() => {
    try {
      // Record game completion for progression (counts toward "days played" either way)
      recordGamePlayed(currentMode)

      trackPageView(location.pathname, {
        game_mode: currentMode,
        result: won ? 'won' : 'lost',
        guess_count: guesses.length,
        ...(targetWord ? { target_word: targetWord } : {}),
      })

      // Track Hotjar game completion event
      trackEvent('game_completed', {
        game_mode: currentMode,
        result: won ? 'won' : 'lost',
        guess_count: guesses.length,
        ...(targetWord ? { target_word: targetWord } : {}),
        timestamp: Date.now(),
      })
    } catch (error) {
      console.error('Failed to track completion page view:', error)
    }
  }, [location.pathname, currentMode, won, guesses.length, targetWord])

  // If there's no state, redirect to Zword's mode select
  useEffect(() => {
    if (!location.state) {
      navigate('/zword')
    }
  }, [location.state, navigate])

  // Handle back to modes
  const handleBackToModes = () => {
    // Track Hotjar event for returning to mode selection
    trackEvent('return_to_modes', {
      from_game_mode: currentMode,
      game_result: won ? 'won' : 'lost',
    })

    // Navigate with a clean state (no error messages)
    navigate('/zword', { state: {} })
  }

  // Switch to a different mode
  const switchToMode = (mode: WordLength) => {
    // Track Hotjar event for switching modes after completion
    trackEvent('switch_mode_after_completion', {
      from_game_mode: currentMode,
      to_game_mode: mode,
      previous_result: won ? 'won' : 'lost',
    })

    updateSettings({ selectedMode: mode })
    // Navigate with a clean state (no error messages)
    navigate(`/zword/play/${mode}`, { state: {} })
  }

  // Get other available modes to suggest - only those not played today
  const otherModes = Object.keys(GAME_MODES)
    .map(Number)
    .filter((mode) => mode !== currentMode)
    .filter(
      (mode): mode is WordLength =>
        (mode === 3 || mode === 4 || mode === 5 || mode === 6) &&
        !hasModeBeenPlayed(mode as WordLength)
    )
    .sort((a, b) => a - b)

  // Animation variants for staggered appearance
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  }

  // Calculate next UTC midnight
  const now = new Date()
  const tomorrow = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
  )

  // Format the reset time in a user-friendly way
  const formatResetTime = (utcDate: Date) => {
    // Convert UTC time to local time for display
    const localDate = new Date(utcDate.getTime())

    const hours = localDate.getHours()
    const minutes = localDate.getMinutes()

    // Convert to 12-hour format
    const hour12 = hours % 12 || 12
    const ampm = hours >= 12 ? 'pm' : 'am'

    // Add leading zero to minutes if needed
    const minutesStr = minutes < 10 ? `0${minutes}` : minutes

    return `${hour12}:${minutesStr}${ampm}`
  }

  const resetTimeStr = formatResetTime(tomorrow)

  const handleShareResults = () => {
    // Track Hotjar event for sharing results
    trackEvent('share_results', {
      game_mode: currentMode,
      result: won ? 'won' : 'lost',
      guess_count: guesses.length,
    })

    const shareBtn = document.querySelector(
      '[data-share-button="true"]'
    ) as HTMLElement
    if (shareBtn) shareBtn.click()
  }

  const handleViewStats = () => {
    // Track Hotjar event for viewing statistics
    trackEvent('view_statistics_from_completion', {
      game_mode: currentMode,
      result: won ? 'won' : 'lost',
    })
    navigate('/zword/statistics')
  }

  return (
    <div className="mode-selection" data-hj-allow="true">
      <motion.h1
        className="mode-title"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {won ? 'Magnificent!' : 'Good Try!'}
      </motion.h1>

      <motion.p
        className="mode-subtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {won ? 'Well Done!' : 'Better luck next time'}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="mb-8 max-w-3xl mx-auto"
      >
        {/* Display the completed guess grid */}
        <div className="mb-8 flex justify-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <GuessGrid guesses={guesses as Tile[][]} />
          </motion.div>
        </div>

        {/* Daily limit info and actions - centered section */}
        <div className="flex flex-col items-center">
          {(!won || definition) && (
            <div className="mb-6 text-center">
              {!won && (
                <>
                  <p className="text-2xl font-bold text-white/80 mb-2">
                    The word was:
                  </p>
                  <p
                    className="text-4xl md:text-5xl font-black uppercase tracking-widest inline-block px-6 py-2"
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {targetWord.toUpperCase()}
                  </p>
                </>
              )}
              {definition && (
                <p className="text-base text-white/60 max-w-md mx-auto mt-2">
                  Meaning: {definition}
                </p>
              )}
            </div>
          )}

          {secondsTaken !== null && (
            <p className="text-white/50 text-sm mb-6">
              {won ? 'Solved' : 'Played'} in {formatSecondsTaken(secondsTaken)}
            </p>
          )}

          {/* Setter Note Reveal */}
          {setterNote && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
              className="mb-8 p-6 bg-zinc-950/80 border border-zinc-900 rounded-3xl w-full max-w-sm text-left shadow-2xl relative overflow-hidden backdrop-blur-md"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-violet-600/10 rounded-full blur-2xl" />
              
              <div className="flex justify-between items-start border-b border-zinc-900 pb-3 mb-3">
                <div>
                  <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest block">Daily Setter</span>
                  <span className="text-sm font-extrabold text-violet-400">{setterNote.penName}</span>
                </div>
                <img
                  src={`${API_BASE_URL}${setterNote.signatureUrl}`}
                  alt={`${setterNote.penName}'s Signature`}
                  className="h-10 object-contain invert opacity-90"
                />
              </div>

              <p className="text-zinc-300 text-sm italic font-serif leading-relaxed pl-3 border-l-2 border-violet-500/50">
                "{setterNote.note}"
              </p>
            </motion.div>
          )}

          <div className="mb-4 w-full max-w-md">
            <StreakCard />
          </div>

          <SaveProgressPrompt won={won} />

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <button
              onClick={handleShareResults}
              className="play-button"
              data-hj-clickable="true"
            >
              Share Results
            </button>
            <button
              onClick={handleViewStats}
              className="play-button bg-[#2a2a2a] hover:bg-[#333] border border-[#444]"
              data-hj-clickable="true"
            >
              View Leaderboard
            </button>
          </div>

          {/* Daily limit info */}
          {otherModes.length > 0 && (
            <div className="mb-6 w-full max-w-md p-4 bg-[rgba(92,18,22,0.4)] rounded-lg border border-[rgba(237,28,36,0.2)]">
              <p className="text-center text-white/80 text-sm">
                New {currentMode}-letter challenge available at {resetTimeStr}
              </p>
            </div>
          )}
        </div>

        {/* Completely hidden share button */}
        <div
          className="hidden absolute"
          aria-hidden="true"
          style={{ opacity: 0, position: 'absolute', left: '-9999px' }}
        >
          <ShareButton guesses={guesses} won={won} />
        </div>
      </motion.div>

      {/* Other Challenges Section - Only shown if there are unplayed modes */}
      {otherModes.length > 0 && (
        <>
          <motion.h2
            className="mode-title text-2xl mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            Try Other Challenges
          </motion.h2>

          <motion.div
            className="mode-cards"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {otherModes.map((mode) => (
              <motion.div key={mode} variants={item}>
                <div className="mode-card" data-hj-clickable="true">
                  <h2>{mode}-Letter Challenge</h2>
                  <p>Test your skills with {mode}-letter words</p>
                  <div className="text-center mt-auto">
                    <button
                      onClick={() => switchToMode(mode)}
                      className="play-button"
                    >
                      Play Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.div variants={item}>
              <div className="mode-card" data-hj-clickable="true">
                <h2>More Coming Soon</h2>
                <p>We're working on new exciting challenges for you!</p>
                <div className="text-center mt-auto">
                  <button onClick={handleBackToModes} className="play-button">
                    Back to Mode Selection
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}

      {/* Show a message if all modes have been played */}
      {otherModes.length === 0 && (
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <p className="text-lg text-white/80 mb-4">
            You've completed all of today's challenges!
          </p>
          <p className="text-sm text-white/60 mb-6">
            New challenges will be available at {resetTimeStr}
          </p>
          <button onClick={handleBackToModes} className="play-button">
            Back to Mode Selection
          </button>
        </motion.div>
      )}
    </div>
  )
}
